import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity from '../../../core/models/business.model';
import { FarmSiteService } from '../../../core/services/farm-site.service';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import { PondSectionService } from '../../../core/services/pond-section.service';
import PondSectionModel from '../../../core/models/pond-section.model';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import { PondCultureService } from '../../../core/services/pond-culture.service';
import PondCultureModel from '../../../core/models/pond-culture.model';
import * as moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { ExcelService } from 'src/app/core/services/excel.service';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import { USER_SESSION_SERVICE } from 'src/app/core/rbacl/user-session.service';
import { BUSINESS_CATEGORY_KEYS, PERSON_ROLE_KEYS } from 'src/app/core/constants/app-constants';

declare var $: any;

/**
 * DaySummaryComponent
 *
 * This component is responsible for generating and displaying daily summary reports for farm operations.
 * It provides a user interface for selecting business, farm site, pond section, and date to view detailed
 * daily metrics including feed logs, water quality, and treatments.
 *
 * Key Responsibilities:
 * - Fetch and display daily summary reports based on selected criteria.
 * - Provide hierarchical selection (business → farm site → section → pond).
 * - Filter and search report data by multiple criteria.
 * - Export report data to Excel.
 * - Implement infinite scrolling for efficient data display.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * User Roles:
 * - Farmer: Can view reports for their own farm business.
 * - Farm Manager: Can view reports for assigned farm business.
 * - Business Admin (Farm): Can view reports for all farms in their business.
 * - Business Admin (Provider): Can view reports for all farmer businesses.
 * - Provider Manager: Can view reports for farms assigned to their technicians.
 * - Employee: Can view reports for all businesses (with search).
 *
 * Features:
 * - **Report Generation**: View daily summary metrics for specific ponds and dates.
 * - **Dynamic Filtering**: Filter by section, pond, or search text.
 * - **Excel Export**: Download the report data in Excel format.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Role-based Access**: Restricts data access based on user role and business category.
 * - **Fullscreen View**: View report data in fullscreen mode.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Loads business and farm site data based on user role.
 * 3. User selects business, farm site, and date.
 * 4. `daySummary()` fetches report data from `PondCultureService`.
 * 5. Data is displayed in a table with infinite scroll support.
 * 6. User can filter, search, or export the data.
 *
 * @component
 * @selector app-day-summary
 * @example
 * <app-day-summary></app-day-summary>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-day-summary',
  templateUrl: './day-summary.component.html',
  styleUrls: ['./day-summary.component.scss']
})
export class DaySummaryComponent implements OnInit, OnDestroy {
  /**
   * Array of business entities available for selection.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Currently selected business entity.
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;

  /**
   * Array of farm sites associated with the selected business.
   * @type {Array<FarmSiteModel>}
   */
  public farmSites: Array<FarmSiteModel>;

  /**
   * Array of pond sections available in the selected farm site.
   * @type {Array<PondSectionModel>}
   */
  public pondSections: Array<PondSectionModel>;

  /**
   * Array of customer ponds in the selected section.
   * @type {Array<CustomerPondModel>}
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * Array of pond culture records (report data).
   * @type {Array<PondCultureModel>}
   */
  public pondCultures: Array<PondCultureModel>;

  /**
   * Array of currently displayed pond culture records (filtered/paginated).
   * @type {Array<PondCultureModel>}
   */
  public pondCulturesList: Array<PondCultureModel>;

  /**
   * Model for the current pond culture report request.
   * @type {PondCultureModel}
   */
  public pondCulture: PondCultureModel = new PondCultureModel();

  /**
   * Duplicate array of pond culture records (for filtering reference).
   * @type {Array<PondCultureModel>}
   */
  public dupPondCultures: Array<PondCultureModel>;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Business name of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessName: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessCategory: string;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  public responseLoader: boolean;

  /**
   * Flag indicating if a search has been executed.
   * @type {boolean}
   */
  public isSearchClicked: boolean;

  /**
   * Dropdown options for pond details.
   * @type {any[]}
   */
  public pondsDetails_dropdown: any[];

  /**
   * Selected transaction date.
   * @type {any}
   */
  public transactionDate: any;

  /**
   * Application permissions object.
   * @type {any}
   */
  public appPermission: any;
  /* End pagination, sorting, filter*/
  /**
   * Keyword used for filtering/searching.
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Current search query string.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Active page number.
   * @type {number}
   */
  public activePageNo: number;

  /**
   * Flag to trigger clearing of search input.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Array of persons (technicians) for provider manager role.
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;

  /**
   * Reactive form group for report criteria selection.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag for business dropdown open/close state.
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;

  /**
   * Filtered options for business dropdown.
   * @type {any[]}
   */
  public filteredOptions: any[] = [];

  /**
   * Placeholder text for business search input.
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';

  /**
   * Flag indicating if the form has been submitted.
   * @type {boolean}
   */
  public submitted: boolean;

  /**
   * Flag to disable the submit button during async operations.
   * @type {boolean}
   * @default false
   */
  public submitButtonDisabled: boolean = false;

  /**
   * Type of filter applied (e.g., 'search', 'section', 'pond').
   * @type {string}
   */
  public filterType: string;

  /**
   * Flag indicating if filters have been applied.
   * @type {boolean}
   */
  public filtersApplied: boolean;

  /**
   * Flag indicating if the submit button was clicked.
   * @type {boolean}
   */
  public isSubmitClicked: boolean;

  /**
   * Flag indicating if a pond is selected for filtering.
   * @type {boolean}
   */
  public isPondSelected: boolean;

  /**
   * Flag indicating if a date range is selected (not actively used in current logic).
   * @type {boolean}
   */
  public isDateRangeSelected: boolean;

  /**
   * Flag indicating if a section is selected for filtering.
   * @type {boolean}
   */
  public isSectionSelected: boolean;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;                 // Load this many per scroll

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Form control for the transaction date picker.
   * @type {FormControl}
   */
  transactionDateControl = new FormControl(new Date(), Validators.required);

  /**
   * Formatted date string for display.
   * @type {string}
   */
  formattedDate: string;

  /**
   * Maximum selectable date (today).
   * @type {Date}
   */
  maxDate: Date = new Date();

  /**
   * Constructor for DaySummaryComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - businessfield: Required.
   * - locationField: Required.
   * - date: Required.
   *
   * @constructor
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {ExcelService} excelService - Service for Excel export operations.
   * @service ExcelService
   * @param {PersonsService} personService - Service for managing person data.
   * @service PersonsService
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {FarmSiteService} farmSitesService - Service for Farm Site API operations.
   * @service FarmSiteService
   * @param {PondSectionService} pondSectionService - Service for Pond Section API operations.
   * @service PondSectionService
   * @param {CustomerPondService} customerPondService - Service for Customer Pond API operations.
   * @service CustomerPondService
   * @param {PondCultureService} pondCultureService - Service for Pond Culture API operations.
   * @service PondCultureService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public personService: PersonsService, public route: ActivatedRoute, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public pondSectionService: PondSectionService, public customerPondService: CustomerPondService, public pondCultureService: PondCultureService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.pondCulture = new PondCultureModel();
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
      date: this.transactionDateControl,
    });
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Retrieves user details from local storage.
   * 3. Resets component state flags.
   * 4. Loads business and farm site data based on user role (Farmer vs Admin vs Provider).
   * 5. Auto-selects farm site if only one is available.
   * 6. Loads business entities for selection if user is an employee/admin.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      switch (USER_SESSION_SERVICE.currentBusinessCategoryName) {
        case BUSINESS_CATEGORY_KEYS.PROVIDER:
          switch (USER_SESSION_SERVICE.currentUserRole) {
            case PERSON_ROLE_KEYS.BUSINESS_ADMIN:
              this.pondCulture.business_id = USER_SESSION_SERVICE.currentBusinessId;
              this.farmSites = await this.farmSitesService.getFarmeSitesBusinessDetailsByProviderId(USER_SESSION_SERVICE.currentBusinessId);
              if (this.farmSites.length === 1) {
                this.pondCulture.farm_site_id = this.farmSites[0].id; // model-binding key
                this.getSetionsByFarmsiteId(this.pondCulture.farm_site_id);
              }
              this.registerForm.get('businessfield').setValidators(null);
              this.registerForm.get('businessfield').updateValueAndValidity();
              break;
            default:
              this.persons = await this.personService.getAllTechniciansByProviderManagerId(USER_SESSION_SERVICE.currentUserId);
              break;
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Fetches pond sections for the selected farm site.
   * Auto-selects section if only one is available.
   *
   * @async
   * @method getSetionsByFarmsiteId
   * @param {string} farmSiteId - The ID of the farm site.
   * @returns {Promise<void>}
   */
  public async getSetionsByFarmsiteId(farmSiteId: string) {
    try {
      this.pondCulture.section_id = undefined;
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmSiteId);
      if (this.pondSections.length == 1) {
        this.pondCulture.section_id = this.pondSections[0].id; // model-binding key
        this.getPondsBySectionId(this.pondCulture.section_id);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Fetches active ponds for the selected section.
   * Populates the pond dropdown with active ponds and preparatory ponds.
   *
   * @async
   * @method getPondsBySectionId
   * @param {string} sectionId - The ID of the pond section.
   * @returns {Promise<void>}
   */
  public async getPondsBySectionId(sectionId: string) {
    this.pondCulture.pond_id = undefined;
    this.pondsDetails_dropdown = [];
    this.isPondSelected = false;
    if (this.pondCulture.section_id) {
      try {
        this.customerPonds = await this.customerPondService.getPondsBySectionId(sectionId);
        this.pondsDetails_dropdown = [];
        if (this.customerPonds && this.customerPonds.length > 0) {
          for (let i = 0; i < this.customerPonds.length; i++) {
            // let eachrec = this.customerPonds[i];
            if (this.customerPonds[i].PondCultureData && this.customerPonds[i].PondCultureData.length > 0) {
              let eachpondculture = this.customerPonds[i].PondCultureData[this.customerPonds[i].PondCultureData.length - 1];
              if (eachpondculture.end_date == null) { // active ponds
                let activePondsObj = {
                  id: eachpondculture.id,
                  pond_id: this.customerPonds[i].id,
                  pond_name: this.customerPonds[i].name,
                };
                this.pondsDetails_dropdown.push(activePondsObj);
              }
            }
            if (this.customerPonds[i].isPreparatory == true) {
              let prepObj = {
                pond_id: this.customerPonds[i].id,
                pond_name: this.customerPonds[i].name,
              };
              this.pondsDetails_dropdown.push(prepObj);
            }
          }
        }
        if (this.pondsDetails_dropdown && this.pondsDetails_dropdown.length == 1) {
          this.pondCulture.pond_id = this.pondsDetails_dropdown[0].pond_id; // model-binding key
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
      }
    }
  }

  /**
   * Fetches farm sites assigned to a technician (Provider Manager role).
   *
   * @async
   * @method getFarmSitesByTechnicianId
   * @returns {Promise<void>}
   */
  public async getFarmSitesByTechnicianId() {
    try {
      this.pondCulture.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByTechnicianId(this.pondCulture.person_id);
      if (this.farmSites.length === 1) {
        this.pondCulture.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Getter for easy access to registerForm controls.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * Filters business options based on search text.
   *
   * @method filterBusinesses
   * @returns {void}
   * @side-effect Updates filteredOptions array.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown.
   * Sets the selected business ID and fetches associated farm sites.
   *
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity.
   * @returns {Promise<void>}
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.pondCulture.business_id = business.id;
    if (this.pondCulture.business_id) {
      this.pondCulture.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(this.pondCulture.business_id);
      if (this.farmSites.length === 1) {
        this.pondCulture.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    }
  }

  /**
   * Resets filter variables when filter type changes.
   *
   * @method onFilterChange
   * @returns {void}
   */
  public onFilterChange() {
    this.filterType = '';
    this.emptyFilterVariables();
  }

  /**
   * Clears section and pond selection filters.
   *
   * @method emptyFilterVariables
   * @returns {void}
   */
  public emptyFilterVariables() {
    this.isSectionSelected = false;
    this.isPondSelected = false;
    this.pondCulture.pond_id = '';
    this.pondCulture.section_id = '';
  }

  /**
   * Applies filters and search to the report data.
   *
   * - Handles search text filtering.
   * - Handles section and pond filtering.
   * - Updates `pondCulturesList` or resets batch loader.
   *
   * @method onFiltersApplied
   * @returns {void}
   */
  public onFiltersApplied() {
    this.pondCulturesList = [];
    this.isFiltering = true;
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.filterType == '') {
        this.applySearchWithFilterType(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.filterType == '') {
        this.applySearchWithFilterType(value);
      } else {
        // this.pondCultures = this.dupPondCultures;
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * Filters report data based on search text.
   *
   * @private
   * @method applySearchFilter
   * @param {string} searchValue - The search text.
   */
  private applySearchFilter(searchValue: string) {
    this.pondCulturesList = this.pondCultures.filter((dayWiseSummaryDetailsObj: any) =>
      ['section_name', 'Tank'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], dayWiseSummaryDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * Filters report data based on search text and selected filters (section/pond).
   *
   * @private
   * @method applySearchWithFilterType
   * @param {string} searchValue - The search text.
   */
  private applySearchWithFilterType(searchValue: string) {
    this.pondCulturesList = this.pondCultures.filter((dayWiseSummaryDetailsObj: any) => {
      let includeConditions = true; // Initialize shouldKeep as true
      if (this.isSectionSelected && this.pondCulture.section_id && dayWiseSummaryDetailsObj.section_id != Number(this.pondCulture.section_id)) {
        includeConditions = false;
      }
      if (this.isPondSelected && this.pondCulture.pond_id && dayWiseSummaryDetailsObj.pond_id != Number(this.pondCulture.pond_id)) {
        includeConditions = false; // Set shouldKeep to false if condition is not met
      }
      const includesSearch = ['section_name', 'Tank'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], dayWiseSummaryDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
      return this.search != '' ? includeConditions && includesSearch : includeConditions;
    });
  }

  /**
   * Clears applied filters and resets the view.
   *
   * @method clearFilters
   * @param {string} filterFrom - The source of the clear action ('search' or 'default').
   * @returns {void}
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.filterType = undefined;
        this.emptyFilterVariables();
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * Handles section selection change for filtering.
   *
   * @method onSectionSelectionChange
   * @param {string} value - The selected section ID.
   * @returns {void}
   */
  public onSectionSelectionChange(value: string): void {
    this.isSectionSelected = !!value;
    this.pondCulture.section_id = value || '';     // '' or a real value
    this.onFiltersApplied();
    this.getPondsBySectionId(this.pondCulture.section_id)
  }

  /**
   * Handles pond selection change for filtering.
   *
   * @method onPondSelectionChange
   * @param {string} value - The selected pond ID.
   * @returns {void}
   */
  public onPondSelectionChange(value: string): void {
    this.isPondSelected = !!value;
    this.pondCulture.pond_id = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * Fetches the day summary report data.
   *
   * - Validates the form.
   * - Prepares the request body with business, farm site, and date.
   * - Calls `pondCultureService.getDaySummaryReport`.
   * - Initializes the batch loader with the fetched data.
   *
   * @async
   * @method daySummary
   * @returns {Promise<void>}
   */
  public async daySummary() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.pondCultures = [];
    this.pondCulturesList = [];
    this.dupPondCultures = [];
    this.isSubmitClicked = false;
    this.onFilterChange();
    this.search = '';
    this.filterType = undefined;
    this.isFiltering = false;
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      if (USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.PROVIDER_MANAGER) {
        let businessId = this.farmSites.find(siteid => siteid.id == this.pondCulture.farm_site_id);
        this.pondCulture.business_id = businessId.businessID;
      }
      const reqBody = {
        "businessName": USER_SESSION_SERVICE.currentUserRole == PERSON_ROLE_KEYS.EMPLOYEE ? this.businessSearch : USER_SESSION_SERVICE.currentBusinessName,
        "farm_site_id": this.pondCulture.farm_site_id,
        "business_id": this.pondCulture.business_id,
        "date": moment(this.transactionDateControl.value).format('YYYY-MM-DD HH:mm:ss')
      }
      if (USER_SESSION_SERVICE.currentUserRole != PERSON_ROLE_KEYS.EMPLOYEE) {
        delete reqBody.business_id;
        delete reqBody.businessName;
      }
      this.pondCultures = await this.pondCultureService.getDaySummaryReport(reqBody);
      if (this.pondCultures?.length) {
        if (this.pondCultures.length > 0) {
          for (let i = 0; i < this.pondCultures.length; i++) {
            if (this.pondCultures[i].TreatmentData != null) {
              this.pondCultures[i].TreatmentData = this.pondCultures[i].TreatmentData.split(',').join('\n');
            }
          }
          // this.dupPondCultures = this.pondCultures;
          this.initializeBatchLoader(); // Reset instance
          this.batchLoader.reset();     // Clear state
          await this.batchLoader.loadNextBatch(true); // Load first batch
        }
      }
      this.responseLoader = false;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.pondCultures,
      (updatedList) => {
        this.pondCulturesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Opens the report view in fullscreen mode.
   *
   * @method openFullScreen
   * @returns {void}
   */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * Exports the day summary report to Excel.
   *
   * @async
   * @method exportExcel
   * @returns {Promise<void>}
   */
  public async exportExcel() {
    try {
      const reqBody = {
        "business_id": this.pondCulture.business_id,
        "farm_site_id": this.pondCulture.farm_site_id,
        "businessName": USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.EMPLOYEE ? this.businessSearch : USER_SESSION_SERVICE.currentBusinessName,
        "farmNameAtLocation": this.farmSites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.pondCulture.farm_site_id).farmNameAtLocation,
        "date": moment(this.transactionDateControl.value).format('YYYY-MM-DD HH:mm:ss'),
        "personName": USER_SESSION_SERVICE.currentUserFullName,
        "mobileNumber": USER_SESSION_SERVICE.currentUserMobileNumber
      }
      const fileObj = this.pondCultureService.excelReportsDownload('daySummary', reqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Day Summary Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
